import { cn } from "@/lib/utils";
import { ReactNode } from "react";
import { motion } from "framer-motion";

interface SectionProps {
  children: ReactNode;
  className?: string;
  id?: string;
  background?: "default" | "muted" | "primary" | "dark" | "light";
  spacing?: "default" | "large" | "compact";
}

export function Section({ children, className, id, background = "default", spacing = "default" }: SectionProps) {
  // All body sections default to dark/tinted to match the cinematic system.
  // `light` is opt-in for the rare editorial light band.
  const bgClasses = {
    default: "dark bg-[#0a0a0f] text-white",
    muted: "dark bg-[#0e0e16] text-white",
    dark: "dark bg-[#08080c] text-white",
    primary: "dark bg-primary text-primary-foreground",
    light: "bg-[#FBFAF6] text-foreground",
  };

  const spacingClasses = {
    compact: "py-12 md:py-16",
    default: "py-20 md:py-28",
    large: "py-24 md:py-36",
  };

  const isDarkVariant = background === "default" || background === "muted" || background === "dark";

  return (
    <section
      id={id}
      className={cn(
        spacingClasses[spacing],
        bgClasses[background],
        "relative isolate overflow-hidden",
        className
      )}
    >
      {isDarkVariant && (
        <>
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 pointer-events-none"
            style={{
              background:
                background === "muted"
                  ? "radial-gradient(55% 45% at 85% 15%, hsl(40 52% 58% / 0.10), transparent 65%), radial-gradient(50% 50% at 10% 95%, hsl(248 70% 30% / 0.22), transparent 65%)"
                  : "radial-gradient(60% 50% at 12% 8%, hsl(248 70% 30% / 0.28), transparent 65%), radial-gradient(45% 50% at 90% 90%, hsl(40 52% 58% / 0.12), transparent 65%)",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 opacity-[0.05] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "180px 180px",
            }}
          />
        </>
      )}
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-6xl relative">
        {children}
      </div>
    </section>
  );
}

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  align?: "left" | "center";
  className?: string;
  variant?: "light" | "dark";
}

export function SectionHeader({
  title,
  subtitle,
  eyebrow,
  align = "center",
  className,
  variant = "dark",
}: SectionHeaderProps) {
  // variant="dark" means rendered ON dark bg (white text). Default since most sections are now dark.
  const titleColor = variant === "dark" ? "text-white" : "text-primary";
  const subColor = variant === "dark" ? "text-white/65" : "text-foreground/65";
  return (
    <motion.div
      className={cn("mb-12 md:mb-16", align === "center" && "text-center", className)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
      viewport={{ once: true, margin: "-80px" }}
    >
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent mb-4">
          {eyebrow}
        </p>
      )}
      <h2
        className={cn(
          "font-display font-bold tracking-[-0.025em] leading-[1.05] text-balance text-3xl md:text-4xl lg:text-5xl",
          titleColor
        )}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={cn(
            "mt-6 text-base md:text-lg max-w-2xl leading-[1.7]",
            subColor,
            align === "center" && "mx-auto"
          )}
        >
          {subtitle}
        </p>
      )}
    </motion.div>
  );
}
